import { cacheService } from './cacheService';
import { supabase } from '../lib/supabase';

const API_BASE_URL = 'https://mobile.padrinho.app';

export interface CachedClinicData {
  id: string;
  name: string;
  description?: string;
  logo_path?: string;
  website?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  phone?: string;
  email?: string;
  whatsapp?: string;
  instagram?: string;
  facebook?: string;
  schedule?: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CachedUserPlan {
  plan_type: string;
  status: string;
  activated_at?: string;
  expires_at?: string;
  features: string[];
}

class ClinicCacheService {
  private readonly CLINIC_KEY_PREFIX = 'clinic:';
  private readonly PLAN_KEY_PREFIX = 'plan:';

  /**
   * Get clinic data for a specific clinic ID
   * Uses API endpoint to bypass RLS issues
   */
  async getClinicData(clinicId: string, accessToken?: string): Promise<CachedClinicData | null> {
    if (!clinicId) {
      console.warn('⚠️ ClinicCacheService: No clinicId provided');
      return null;
    }

    const key = `${this.CLINIC_KEY_PREFIX}${clinicId}`;
    
    try {
      console.log(`🏥 ClinicCacheService: Getting clinic data for ID: ${clinicId}`);
      
      return await cacheService.get(
        key,
        async () => {
          console.log(`🏥 Fetching fresh clinic data via API for ID: ${clinicId}`);
          
          // Get access token if not provided
          let token = accessToken;
          if (!token) {
            const { data: { session } } = await supabase.auth.getSession();
            token = session?.access_token;
          }
          
          if (!token) {
            console.warn('⚠️ No access token available for clinic API request');
            throw new Error('No authentication token available');
          }
          
          // Use API endpoint (bypasses RLS issues)
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
          
          try {
            const response = await fetch(`${API_BASE_URL}/api/clinic/${clinicId}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              signal: controller.signal,
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.error('❌ API error fetching clinic data:', {
                status: response.status,
                error: errorData.error,
                clinicId
              });
              throw new Error(errorData.error || `API error: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (!result.success || !result.clinic) {
              console.warn(`⚠️ No clinic found for ID: ${clinicId}`);
              throw new Error('Clinic not found or inactive');
            }

            console.log('✅ Clinic data fetched successfully via API:', {
              clinicId: result.clinic.id,
              name: result.clinic.name,
              isActive: true
            });
            
            return result.clinic as CachedClinicData;
          } catch (fetchError: any) {
            clearTimeout(timeoutId);
            if (fetchError.name === 'AbortError') {
              console.warn(`⚠️ Clinic API fetch timeout for ID: ${clinicId}`);
              throw new Error('Clinic fetch timeout after 10s');
            }
            throw fetchError;
          }
        },
        cacheService.getConfig('CLINIC')
      );
    } catch (error) {
      console.error(`❌ ClinicCacheService: Error getting clinic data for ${clinicId}:`, {
        error: error instanceof Error ? error.message : String(error),
        clinicId,
        timestamp: new Date().toISOString()
      });
      return null;
    }
  }

  /**
   * Get clinic data for the current authenticated user
   * Uses API endpoints to bypass RLS issues
   */
  async getUserClinicData(): Promise<CachedClinicData | null> {
    try {
      console.log('🏥 ClinicCacheService: Getting user clinic data via API...');
      
      // Get session for auth token
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        console.warn('⚠️ No authenticated session for clinic lookup');
        return null;
      }
      
      const user = session.user;
      const accessToken = session.access_token;

      console.log(`👤 ClinicCacheService: Looking up clinic for user: ${user.id.substring(0, 8)}...`);

      // Get user profile via API (with caching)
      const profileKey = `user:profile:${user.id}`;
      const profile = await cacheService.get(
        profileKey,
        async () => {
          console.log(`👤 Fetching user profile via API for clinic lookup: ${user.id.substring(0, 8)}...`);
          
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          
          try {
            const response = await fetch(`${API_BASE_URL}/api/auth/profile/me`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
              signal: controller.signal,
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.error('❌ API error fetching profile for clinic lookup:', {
                status: response.status,
                error: errorData.error,
                userId: user.id.substring(0, 8) + '...'
              });
              throw new Error(errorData.error || `API error: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (!result.success || !result.profile) {
              console.warn('⚠️ No profile found via API');
              throw new Error('User profile not found');
            }

            console.log('✅ User profile fetched via API for clinic lookup:', {
              hasClinicId: !!result.profile.clinic_id,
              email: result.profile.email,
              userId: user.id.substring(0, 8) + '...'
            });

            return result.profile;
          } catch (fetchError: any) {
            clearTimeout(timeoutId);
            if (fetchError.name === 'AbortError') {
              throw new Error('Profile API fetch timeout after 10s');
            }
            throw fetchError;
          }
        },
        cacheService.getConfig('USER')
      );

      if (!profile?.clinic_id) {
        console.warn('⚠️ User has no associated clinic_id in profile');
        return null;
      }

      console.log(`🏥 ClinicCacheService: User has clinic_id: ${profile.clinic_id}`);

      // Get clinic data using clinic_id with the access token
      const clinicData = await this.getClinicData(profile.clinic_id, accessToken);
      
      if (clinicData) {
        console.log('✅ User clinic data retrieved successfully via API:', {
          clinicName: clinicData.name,
          clinicId: clinicData.id
        });
      } else {
        console.warn('⚠️ Failed to retrieve clinic data despite having clinic_id');
      }

      return clinicData;
    } catch (error) {
      console.error('❌ ClinicCacheService: Error getting user clinic data:', {
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
      return null;
    }
  }

  /**
   * Get user plan information
   */
  async getUserPlan(userId: string): Promise<CachedUserPlan | null> {
    const key = `${this.PLAN_KEY_PREFIX}${userId}`;
    
    try {
      return await cacheService.get(
        key,
        async () => {
          console.log(`📋 Fetching plan data for user: ${userId}`);
          
          // TODO: CRITICAL - Replace hardcoded plan data with actual database query
          // This is currently returning dummy data and needs proper implementation
          const planData: CachedUserPlan = {
            plan_type: 'Base',
            status: 'active',
            activated_at: new Date().toISOString(),
            features: [
              'AI Chat with Bill',
              'Sobriety Tracking',
              'Meeting Notifications',
              'Basic Support'
            ]
          };

          console.log('✅ Plan data fetched successfully:', planData.plan_type);
          return planData;
        },
        cacheService.getConfig('PLAN')
      );
    } catch (error) {
      console.error(`❌ ClinicCacheService: Error getting user plan for ${userId}:`, error);
      return null;
    }
  }

  /**
   * Invalidate clinic cache for specific clinic
   */
  async invalidateClinicCache(clinicId: string): Promise<void> {
    const key = `${this.CLINIC_KEY_PREFIX}${clinicId}`;
    await cacheService.invalidate(key);
    console.log(`🗑️ Invalidated clinic cache for: ${clinicId}`);
  }

  /**
   * Invalidate user plan cache
   */
  async invalidateUserPlanCache(userId: string): Promise<void> {
    const key = `${this.PLAN_KEY_PREFIX}${userId}`;
    await cacheService.invalidate(key);
    console.log(`🗑️ Invalidated plan cache for user: ${userId}`);
  }

  /**
   * Invalidate all clinic-related caches
   */
  async invalidateAllClinicCaches(): Promise<void> {
    try {
      await Promise.all([
        cacheService.invalidatePattern(this.CLINIC_KEY_PREFIX),
        cacheService.invalidatePattern(this.PLAN_KEY_PREFIX),
        cacheService.invalidatePattern('user:profile:'), // Clear unified profile cache
        cacheService.invalidatePattern('bill_config:') // Clear Bill config cache since it depends on clinic data
      ]);
      console.log('🗑️ ClinicCacheService: Invalidated all clinic, plan, and Bill config caches');
    } catch (error) {
      console.error('❌ ClinicCacheService: Error invalidating clinic caches:', error);
      throw error;
    }
  }

  /**
   * Invalidate all caches related to a specific user (coordinated invalidation)
   */
  async invalidateUserRelatedCaches(userId: string): Promise<void> {
    if (!userId) {
      console.warn('⚠️ ClinicCacheService: No userId provided for cache invalidation');
      return;
    }

    try {
      console.log(`🗑️ ClinicCacheService: Invalidating all caches for user: ${userId.substring(0, 8)}...`);
      
      await Promise.all([
        // Clear user profile cache (unified key)
        cacheService.invalidate(`user:profile:${userId}`),
        // Clear user-specific plan cache
        cacheService.invalidate(`${this.PLAN_KEY_PREFIX}${userId}`),
        // Clear user-specific Bill config cache
        cacheService.invalidate(`bill_config:user:${userId}`)
      ]);
      
      console.log(`✅ ClinicCacheService: All user-related caches invalidated for: ${userId.substring(0, 8)}...`);
    } catch (error) {
      console.error(`❌ ClinicCacheService: Error invalidating user caches for ${userId.substring(0, 8)}...:`, {
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  }

  /**
   * Preload clinic data for better performance
   */
  async preloadUserClinicData(): Promise<void> {
    try {
      console.log('🚀 Preloading user clinic data...');
      
      const clinicData = await this.getUserClinicData();
      
      if (clinicData) {
        console.log(`✅ Preloaded clinic data: ${clinicData.name}`);
      } else {
        console.log('ℹ️ No clinic data to preload');
      }
    } catch (error) {
      console.error('❌ Error preloading clinic data:', error);
    }
  }
}

// Export singleton instance
export const clinicCacheService = new ClinicCacheService();
export default clinicCacheService;